import { useRef, useState } from 'react';
import { CirclePlus, Image, Plus, Upload } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Button } from 'components/ui/button';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { modelSupportedExtensionsMap } from '../types/model.types';
import { useTranslation } from 'react-i18next';

export interface FileUploadPopoverProps {
  onFilesSelected?: (files: File[]) => void;
}

export function FileUploadPopover({ onFilesSelected }: FileUploadPopoverProps = {}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedModel = useModelStore((state) => state.selectedModel);
  const [clickOnFiles, setClickOnFiles] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      onFilesSelected && onFilesSelected(newFiles);
      setOpen(false); // Close the popover after selecting files
    }
  };

  const handleAddPhotosAndFilesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleAddPhotosClick = (e: React.MouseEvent) => {
    setTimeout(() => handleAddPhotosAndFilesClick(e), 100);
    setClickOnFiles(false);
  };

  const handleAddFilesClick = (e: React.MouseEvent) => {
    setTimeout(() => handleAddPhotosAndFilesClick(e), 100);
    setClickOnFiles(true);
  };

  function getAcceptData() {
    if (!selectedModel) {
      return '';
    }

    return clickOnFiles
      ? modelSupportedExtensionsMap[selectedModel?.modelType].document.join(',')
      : modelSupportedExtensionsMap[selectedModel?.modelType].image.join(',');
  }

  return (
    <div className="rounded-xl">
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 rounded-full border w-8">
              <Plus className="h-5 w-5" />
              <span className="sr-only">{t('ADD_FILES')}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start" side="top" sideOffset={10}>
            <div className="flex flex-col">
              <button
                disabled={true}
                className="flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left disabled:opacity-50"
              >
                <div className="flex h-4 w-4 items-center justify-center">
                  <Upload className="h-4 w-4" />
                </div>
                <span>{t('UPLOAD_FROM_GOOGLE_DRIVE')}</span>
              </button>

              <button
                className="flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left disabled:opacity-50"
                onClick={handleAddPhotosClick}
                disabled={selectedModel?.modelType === 1}
              >
                <div className="flex h-4 w-4 items-center justify-center">
                  <Image className="h-4 w-4" />
                </div>
                <span>{t('ADD_PHOTOS')}</span>
              </button>

              <button
                className="flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left disabled:opacity-50"
                onClick={handleAddFilesClick}
              >
                <div className="flex h-4 w-4 items-center justify-center">
                  <CirclePlus className="h-4 w-4" />
                </div>
                <span>{t('ADD_FILES')}</span>
              </button>

              <input
                type="file"
                accept={getAcceptData()}
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                multiple
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
